/* ==========================================================================
   SawaKart sections, part 2 (P3): <sk-featured-gallery>, <sk-ig-feed>,
   <sk-parallax>, <sk-countup> + theme-editor hooks for the brands marquee.
   Loaded with `defer` after sk-core.js; window.SK is used when present, with
   local fallbacks so these components never break on their own.
   ========================================================================== */
(function () {
  'use strict';

  var RM_QUERY = '(prefers-reduced-motion: reduce)';
  var rmMedia = window.matchMedia ? window.matchMedia(RM_QUERY) : null;

  function sk() { return window.SK || null; }

  function reducedMotion() {
    var api = sk();
    if (api && typeof api.reducedMotion === 'function') {
      try { return !!api.reducedMotion(); } catch (e) { /* fall through */ }
    }
    return !!(rmMedia && rmMedia.matches) ||
      !!(document.body && document.body.hasAttribute('data-reduce-animations'));
  }

  /* IntersectionObserver once, rootMargin on all sides (framer whileInView). */
  function observeOnce(el, margin, cb) {
    var api = sk();
    var done = false;
    var guarded = function () { if (!done && el.isConnected) { done = true; cb(); } };
    if (api && typeof api.observeOnce === 'function') {
      var stop = api.observeOnce(el, margin, guarded);
      return function () { done = true; if (typeof stop === 'function') stop(); };
    }
    if (!('IntersectionObserver' in window)) { guarded(); return function () {}; }
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { io.disconnect(); guarded(); break; }
      }
    }, { rootMargin: margin + 'px', threshold: 0 });
    io.observe(el);
    return function () { done = true; io.disconnect(); };
  }

  /* UnitBezier solver (WebKit), used when SK.bezier is missing. */
  function localBezier(p1x, p1y, p2x, p2y) {
    var cx = 3 * p1x, bx = 3 * (p2x - p1x) - cx, ax = 1 - cx - bx;
    var cy = 3 * p1y, by = 3 * (p2y - p1y) - cy, ay = 1 - cy - by;
    function sx(t) { return ((ax * t + bx) * t + cx) * t; }
    function sy(t) { return ((ay * t + by) * t + cy) * t; }
    function dx(t) { return (3 * ax * t + 2 * bx) * t + cx; }
    function solveX(x) {
      var t = x, i, x2, d2;
      for (i = 0; i < 8; i++) {
        x2 = sx(t) - x;
        if (Math.abs(x2) < 1e-6) return t;
        d2 = dx(t);
        if (Math.abs(d2) < 1e-6) break;
        t = t - x2 / d2;
      }
      var lo = 0, hi = 1;
      t = x;
      while (lo < hi) {
        x2 = sx(t);
        if (Math.abs(x2 - x) < 1e-6) return t;
        if (x > x2) lo = t; else hi = t;
        t = (hi - lo) / 2 + lo;
        if (hi - lo < 1e-7) break;
      }
      return t;
    }
    return function (x) {
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      return sy(solveX(x));
    };
  }

  function bezier(a, b, c, d) {
    var api = sk();
    if (api && typeof api.bezier === 'function') {
      try { var fn = api.bezier(a, b, c, d); if (typeof fn === 'function') return fn; } catch (e) { /* fall back */ }
    }
    return localBezier(a, b, c, d);
  }

  function pad2(n) { return (n < 10 ? '0' : '') + n; }

  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }

  /* ------------------------------------------------------------------------
     <sk-featured-gallery>: thumbnails, crossfade, drag-x swipe (S04 §7.4)
     ------------------------------------------------------------------------ */
  var DRAG_ELASTIC = 0.18;
  var SWIPE_MIN = 50;
  var SPRING_BACK = 'transform .4s cubic-bezier(.25,1,.5,1)';

  class SkFeaturedGallery extends HTMLElement {
    connectedCallback() {
      this.frame = this.querySelector('.sk-fp__frame');
      this.layers = Array.prototype.slice.call(this.querySelectorAll('.sk-fp__layer[data-index]'));
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('.sk-fp__thumb'));
      this.counter = this.querySelector('[data-sk-fp-current]');
      this.count = this.layers.length;
      this.active = 0;
      for (var i = 0; i < this.layers.length; i++) {
        if (this.layers[i].classList.contains('is-active')) { this.active = i; break; }
      }

      this._onClick = this._onClick.bind(this);
      this.addEventListener('click', this._onClick);

      if (this.frame && this.count > 1) {
        this._down = this._down.bind(this);
        this._move = this._move.bind(this);
        this._up = this._up.bind(this);
        this._dragStart = function (e) { e.preventDefault(); };
        this.frame.addEventListener('pointerdown', this._down);
        this.frame.addEventListener('pointermove', this._move);
        this.frame.addEventListener('pointerup', this._up);
        this.frame.addEventListener('pointercancel', this._up);
        this.frame.addEventListener('lostpointercapture', this._up);
        this.frame.addEventListener('dragstart', this._dragStart);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      if (this.frame && this._down) {
        this.frame.removeEventListener('pointerdown', this._down);
        this.frame.removeEventListener('pointermove', this._move);
        this.frame.removeEventListener('pointerup', this._up);
        this.frame.removeEventListener('pointercancel', this._up);
        this.frame.removeEventListener('lostpointercapture', this._up);
        this.frame.removeEventListener('dragstart', this._dragStart);
      }
      this.drag = null;
    }

    go(index) {
      var n = this.count;
      if (n < 2) return;
      var i = ((index % n) + n) % n;
      if (i === this.active) return;
      this.layers[this.active].classList.remove('is-active');
      this.layers[i].classList.add('is-active');
      for (var t = 0; t < this.thumbs.length; t++) {
        this.thumbs[t].setAttribute('aria-current', t === i ? 'true' : 'false');
      }
      if (this.counter) this.counter.textContent = pad2(i + 1);
      this.active = i;
    }

    _onClick(e) {
      var thumb = e.target.closest('.sk-fp__thumb');
      if (!thumb || !this.contains(thumb)) return;
      this.go(parseInt(thumb.getAttribute('data-index'), 10) || 0);
    }

    _down(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this.drag = { id: e.pointerId, x: e.clientX, dx: 0 };
      this.frame.style.transition = 'none';
      try { this.frame.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      if (e.pointerType === 'mouse') e.preventDefault();
    }

    _move(e) {
      var d = this.drag;
      if (!d || e.pointerId !== d.id) return;
      d.dx = e.clientX - d.x;
      this.frame.style.transform = 'translateX(' + (d.dx * DRAG_ELASTIC) + 'px)';
    }

    _up(e) {
      var d = this.drag;
      if (!d || e.pointerId !== d.id) return;
      this.drag = null;
      try { this.frame.releasePointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      this.frame.style.transition = reducedMotion() ? 'none' : SPRING_BACK;
      this.frame.style.transform = 'translateX(0px)';
      if (e.type === 'pointerup' && Math.abs(d.dx) >= SWIPE_MIN) {
        this.go(this.active + (d.dx < 0 ? 1 : -1));
      }
    }
  }
  define('sk-featured-gallery', SkFeaturedGallery);

  /* ------------------------------------------------------------------------
     <sk-ig-feed>: arrow state + scrollBy (S04 §9.4)
     ------------------------------------------------------------------------ */
  class SkIgFeed extends HTMLElement {
    connectedCallback() {
      this.row = this.querySelector('.sk-ig__row');
      this.prev = this.querySelector('.sk-ig__arrow--prev');
      this.next = this.querySelector('.sk-ig__arrow--next');
      if (!this.row) return;
      this._update = this._update.bind(this);
      this._onClick = this._onClick.bind(this);
      this.row.addEventListener('scroll', this._update, { passive: true });
      window.addEventListener('resize', this._update);
      window.addEventListener('load', this._update);
      this.addEventListener('click', this._onClick);
      this._update();
    }

    disconnectedCallback() {
      if (!this.row) return;
      this.row.removeEventListener('scroll', this._update);
      window.removeEventListener('resize', this._update);
      window.removeEventListener('load', this._update);
      this.removeEventListener('click', this._onClick);
    }

    _update() {
      var row = this.row;
      var offset = Math.abs(row.scrollLeft);
      if (this.prev) this.prev.disabled = !(offset > 4);
      if (this.next) this.next.disabled = !(offset + row.clientWidth < row.scrollWidth - 4);
    }

    _onClick(e) {
      var btn = e.target.closest('.sk-ig__arrow');
      if (!btn || !this.contains(btn)) return;
      this.step(btn === this.next ? 1 : -1);
    }

    step(dir) {
      var card = this.row.firstElementChild;
      if (!card) return;
      this.row.scrollBy({ left: (card.offsetWidth + 16) * dir, behavior: 'smooth' });
    }
  }
  define('sk-ig-feed', SkIgFeed);

  /* ------------------------------------------------------------------------
     <sk-parallax data-distance="d">: translateY(d·(1−2p)), one shared loop.
     p = (vh − top) / (vh + h), clamped; top = untransformed document offset
     (offsetTop chain ignores transforms, like framer's useScroll).
     d may be a share of the element's own height ("-6%": banner photos).
     data-mode="exit": translateY(d*q), q = 0 to 1 while the element's top leaves the
     top of the screen (the hero drifting down as it scrolls away, mtstorelb).
     data-media="(min-width: 768px)": moves only while the query matches.
     ------------------------------------------------------------------------ */
  var plxEls = new Set();
  var plxRaf = 0;
  var plxBound = false;
  var plxResizeObserver = null;

  function plxMeasure(el) {
    var top = 0;
    var node = el;
    while (node) { top += node.offsetTop; node = node.offsetParent; }
    el._skTop = top;
    el._skH = el.offsetHeight;
    el._skD = el._skPct === null ? el._skBaseD : el._skPct / 100 * el._skH;
  }

  function plxUpdate() {
    plxRaf = 0;
    var off = reducedMotion();
    var vh = window.innerHeight;
    var sy = window.pageYOffset || document.documentElement.scrollTop || 0;
    plxEls.forEach(function (el) {
      if (off || (el._skMq && !el._skMq.matches)) {
        if (el.style.transform) el.style.transform = '';
        return;
      }
      var top = el._skTop - sy;
      var y;
      if (el._skExit) {
        var q = el._skH ? -top / el._skH : 0;
        q = q < 0 ? 0 : (q > 1 ? 1 : q);
        y = el._skD * q;
      } else {
        var p = (vh - top) / (vh + el._skH);
        p = p < 0 ? 0 : (p > 1 ? 1 : p);
        y = el._skD * (1 - 2 * p);
      }
      el.style.transform = 'translateY(' + y.toFixed(2) + 'px)';
    });
  }

  function plxSchedule() { if (!plxRaf) plxRaf = requestAnimationFrame(plxUpdate); }
  function plxRemeasure() { plxEls.forEach(plxMeasure); plxSchedule(); }

  function plxBind() {
    if (plxBound) return;
    plxBound = true;
    window.addEventListener('scroll', plxSchedule, { passive: true });
    window.addEventListener('resize', plxRemeasure);
    window.addEventListener('load', plxRemeasure);
    if (rmMedia) {
      if (rmMedia.addEventListener) rmMedia.addEventListener('change', plxRemeasure);
      else if (rmMedia.addListener) rmMedia.addListener(plxRemeasure);
    }
    if ('ResizeObserver' in window && document.body) {
      plxResizeObserver = new ResizeObserver(function () { plxRemeasure(); });
      plxResizeObserver.observe(document.body);
    }
  }

  function plxUnbind() {
    if (!plxBound) return;
    plxBound = false;
    window.removeEventListener('scroll', plxSchedule);
    window.removeEventListener('resize', plxRemeasure);
    window.removeEventListener('load', plxRemeasure);
    if (rmMedia) {
      if (rmMedia.removeEventListener) rmMedia.removeEventListener('change', plxRemeasure);
      else if (rmMedia.removeListener) rmMedia.removeListener(plxRemeasure);
    }
    if (plxResizeObserver) { plxResizeObserver.disconnect(); plxResizeObserver = null; }
    if (plxRaf) { cancelAnimationFrame(plxRaf); plxRaf = 0; }
  }

  class SkParallax extends HTMLElement {
    connectedCallback() {
      var dist = this.getAttribute('data-distance') || '';
      this._skBaseD = parseFloat(dist) || 0;
      this._skPct = /%\s*$/.test(dist) ? this._skBaseD : null;
      this._skD = this._skBaseD;
      this._skExit = this.getAttribute('data-mode') === 'exit';
      var media = this.getAttribute('data-media');
      this._skMq = media && window.matchMedia ? window.matchMedia(media) : null;
      plxEls.add(this);
      plxBind();
      plxMeasure(this);
      plxSchedule();
    }

    disconnectedCallback() {
      plxEls.delete(this);
      this.style.transform = '';
      if (!plxEls.size) plxUnbind();
    }
  }
  define('sk-parallax', SkParallax);

  /* ------------------------------------------------------------------------
     <sk-countup data-value data-suffix>: 0 → value over 1.8s on view (-60px)
     ------------------------------------------------------------------------ */
  var COUNT_MS = 1800;

  class SkCountup extends HTMLElement {
    connectedCallback() {
      this._text = this.querySelector('[data-sk-countup-text]');
      this._value = parseFloat(this.getAttribute('data-value'));
      this._suffix = this.getAttribute('data-suffix') || '';
      if (!this._text || !isFinite(this._value) || reducedMotion()) return;
      var self = this;
      this._stop = observeOnce(this, -60, function () {
        if (!reducedMotion()) self._run();
      });
    }

    disconnectedCallback() {
      if (this._stop) { this._stop(); this._stop = null; }
      if (this._raf) { cancelAnimationFrame(this._raf); this._raf = 0; }
    }

    _format(n) { return Math.round(n).toLocaleString('en-US') + this._suffix; }

    _run() {
      var self = this;
      var ease = bezier(0.16, 1, 0.3, 1);
      var start = 0;
      this._text.textContent = this._format(0);
      var tick = function (now) {
        if (!start) start = now;
        var t = Math.min(1, (now - start) / COUNT_MS);
        self._text.textContent = self._format(self._value * ease(t));
        self._raf = t < 1 ? requestAnimationFrame(tick) : 0;
      };
      this._raf = requestAnimationFrame(tick);
    }
  }
  define('sk-countup', SkCountup);

  /* ------------------------------------------------------------------------
     Theme editor: pause the brand marquee while its section or a brand block
     is selected; bring a selected brand / Instagram post into view.
     ------------------------------------------------------------------------ */
  function strips(root) {
    return root && root.querySelectorAll ? root.querySelectorAll('.sk-brands__strip') : [];
  }

  function seekBrand(card) {
    var strip = card.closest('.sk-brands__strip');
    var track = card.closest('.sk-brands__track');
    if (!strip || !track || typeof track.getAnimations !== 'function') return;
    var anim = track.getAnimations()[0];
    if (!anim || !anim.effect) return;
    var half = track.scrollWidth / 2;
    var duration = Number(anim.effect.getTiming().duration) || 0;
    if (!half || !duration) return;
    var offset = Math.max(0, Math.min(half - 1, card.offsetLeft - 80));
    anim.currentTime = (offset / half) * duration;
  }

  document.addEventListener('shopify:section:select', function (e) {
    Array.prototype.forEach.call(strips(e.target), function (s) { s.classList.add('is-paused'); });
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    Array.prototype.forEach.call(strips(e.target), function (s) { s.classList.remove('is-paused'); });
  });
  document.addEventListener('shopify:block:select', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;
    var brand = target.closest('.sk-brand');
    if (brand) {
      var strip = brand.closest('.sk-brands__strip');
      if (strip) strip.classList.add('is-paused');
      seekBrand(brand);
      return;
    }
    var card = target.closest('.sk-ig__card');
    if (card) card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;
    var strip = target.closest('.sk-brands__strip');
    if (strip) strip.classList.remove('is-paused');
  });
})();

/* SK-63: the long text in a .sk-seo-card folds under "Read more" when it is taller than about 300px (shorter
   texts stay open, no button). Without JavaScript the text is simply shown in full. */
(function () {
  'use strict';
  function initMore(root) {
    if (!root || !root.querySelectorAll) return;
    Array.prototype.forEach.call(root.querySelectorAll('[data-sk-more]'), function (box) {
      if (box._skMore) return;
      var btn = box.parentNode && box.parentNode.querySelector('[aria-controls="' + box.id + '"]');
      if (!btn) return;
      box._skMore = true;
      if (box.scrollHeight <= 300) return;
      var label = btn.querySelector('[data-sk-more-label]');
      box.classList.add('is-collapsed');
      btn.hidden = false;
      btn.addEventListener('click', function () {
        var open = box.classList.contains('is-collapsed');
        box.classList.toggle('is-collapsed', !open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (label) label.textContent = open ? 'Read less' : 'Read more';
        if (!open) {
          var card = box.closest('.sk-seo-card') || box;
          var top = card.getBoundingClientRect().top;
          if (top < 0) window.scrollBy({ top: top - 96, behavior: 'smooth' });
        }
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initMore(document); });
  else initMore(document);
  document.addEventListener('shopify:section:load', function (e) { initMore(e.target); });
})();

/* SK-70: the brand strip ships each brand once (search engines and SEO checkers see 20 brand links, not 40);
   the second copy that the endless marquee needs (translateX 0 -> -50%) is made here: hidden from screen
   readers and the keyboard, without the theme editor's block attributes. The track only moves once it is
   doubled (.is-looped, sk-sections-2.css), so without this script the strip simply stands still. */
(function () {
  'use strict';
  function loop(root) {
    var tracks = root && root.querySelectorAll ? root.querySelectorAll('.sk-brands__track:not(.is-looped)') : [];
    Array.prototype.forEach.call(tracks, function (track) {
      var items = Array.prototype.slice.call(track.children);
      if (!items.length) return;
      items.forEach(function (item) {
        var copy = item.cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');
        copy.setAttribute('tabindex', '-1');
        Array.prototype.slice.call(copy.attributes).forEach(function (a) {
          if (a.name.indexOf('data-shopify-editor') === 0) copy.removeAttribute(a.name);
        });
        track.appendChild(copy);
      });
      track.classList.add('is-looped');
    });
  }
  loop(document);
  document.addEventListener('shopify:section:load', function (e) { loop(e.target); });
})();
